/**
 * settlement_dressing.ts — the procedural in-town dressing layer (The Unbuilt
 * Town wave): walls/gates/boundary stones, wayfinding (town signs,
 * fingerposts, plaza map board), rendered wells + market trough, plaza
 * character (civic / market / faith variants) + lantern strings, active
 * frontages (awnings, trade signs, window glow), garden-edge yards with
 * data-shaped clutter, terrace retaining walls + stairs, street trees +
 * pocket greens, and the hillside lookout.
 *
 * Everything here is deterministic low-poly procedural geometry (no GLB
 * loads), instanced per part kind — a settlement's whole dressing layer is a
 * handful of InstancedMeshes + a few unique text planks. The module owns
 * physics/solids/NPC-seat registration from the returned records and drives
 * the shared night materials + windmill rotors per frame.
 *
 * Versona safety laws honored here:
 *  · NPC keep-outs (structure seats) are SMALL discs (≤2.6 m) so a projected
 *    body never lands >6 m from its server position (the talk-range law).
 *  · Nothing is placed inside the shuttle-pad arrival annulus, the port
 *    discs, the venue stages, the plaza ceremony heart or the notice board.
 *  · Long walls are SEGMENTED (many short solids) so ship/hover collision
 *    reads the real line, never one huge box.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { CityPlan } from 'enginex/city/config.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { townSlots, type TownSlot } from '../../../shared/town_layout.js';
import { meshHeight } from './terrain_stamp.js';
import { gatesFor, ceremonialRoute, streetNames, transectBand, h01 } from './districts.js';
import { civilizationOf } from './biome_settlements.js';
import { settlementNamesFor } from './settlement_names.js';
import { spaceportSpot, shuttleSpot, terminalSpot, arrivalPoint } from './ports.js';
import type { LampAnchor } from '../modules/StreetLampsModule.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { buildCapitalWorks } from './capital_works.js';
import { buildCircuitWorks } from './circuit_works.js';

// ── Shared materials (one set for every settlement; never disposed) ─────────

export interface MatSet {
  wallA: THREE.MeshStandardMaterial; wallB: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial; woodDark: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial; stoneDark: THREE.MeshStandardMaterial;
  clothA: THREE.MeshStandardMaterial; clothB: THREE.MeshStandardMaterial;
  clothWhite: THREE.MeshStandardMaterial;
  leafGreen: THREE.MeshStandardMaterial; leafDark: THREE.MeshStandardMaterial;
  soil: THREE.MeshStandardMaterial;
  water: THREE.MeshStandardMaterial;
  glow: THREE.MeshBasicMaterial;       // window glow — opacity ∝ night
  lantern: THREE.MeshBasicMaterial;    // lantern/torch glow — opacity ∝ night
}

let mats: MatSet | null = null;

/** The shared material set (lazy). The two glow materials are night-driven. */
export function dressingMats(): MatSet {
  if (mats) return mats;
  const std = (color: number, rough = 0.9, metal = 0.02): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: metal });
  const glow = new THREE.MeshBasicMaterial({
    color: 0xffcf8a, transparent: true, opacity: 0, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
  const lantern = new THREE.MeshBasicMaterial({
    color: 0xffb45e, transparent: true, opacity: 0, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
  markMrtOverlay(glow); markMrtOverlay(lantern); // window glow OVER the facade — the facade keeps its normal
  mats = {
    wallA: std(0xb09468, 0.95), wallB: std(0x7d8a94, 0.95),
    wood: std(0x8a6b4a, 0.92), woodDark: std(0x5e4632, 0.94),
    stone: std(0x9a9d9f, 0.97), stoneDark: std(0x63666b, 0.97),
    clothA: std(0xd9a94e, 0.85), clothB: std(0x7fc4e8, 0.85),
    clothWhite: std(0xe8e2d4, 0.85),
    leafGreen: std(0x5d7d43, 0.95), leafDark: std(0x3c5537, 0.95),
    soil: std(0x6e573c, 1.0),
    water: std(0x4a7d8c, 0.35, 0.1),
    glow, lantern,
  };
  return mats;
}

/** Night-driven materials — the module sets opacity from uNightFactor. */
export function dressingNightMats(): { glow: THREE.Material; lantern: THREE.Material } {
  const m = dressingMats();
  return { glow: m.glow, lantern: m.lantern };
}

// ── Text planks (canvas textures, cached by content) ────────────────────────

const textTexCache = new Map<string, THREE.CanvasTexture>();

function textTexture(text: string, fg: string, bg: string, font = 44): THREE.CanvasTexture {
  const key = `${text}|${fg}|${bg}|${font}`;
  const hit = textTexCache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = `bold ${font}px Georgia, serif`;
  canvas.width = Math.max(64, Math.ceil(ctx.measureText(text).width) + 36);
  canvas.height = font + 30;
  ctx.font = `bold ${font}px Georgia, serif`;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = fg;
  ctx.lineWidth = 3;
  ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
  ctx.fillStyle = fg;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 18, canvas.height / 2 + 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  textTexCache.set(key, tex);
  return tex;
}

// Materials cached alongside their textures: disposeSettlement frees only
// geometry (materials are assumed shared), so a per-call material leaked one
// pipeline variant per plank / map board on every settlement stream cycle.
const texMatCache = new Map<THREE.Texture, THREE.MeshStandardMaterial>();
function texturedMat(tex: THREE.Texture, roughness: number): THREE.MeshStandardMaterial {
  let m = texMatCache.get(tex);
  if (!m) { m = new THREE.MeshStandardMaterial({ map: tex, roughness, side: THREE.DoubleSide }); texMatCache.set(tex, m); }
  return m;
}

export function plank(text: string, civ: 'a' | 'b', wM: number, hM: number): THREE.Mesh {
  const tex = textTexture(text, civ === 'a' ? '#3a2c12' : '#12262e', civ === 'a' ? '#d9c08a' : '#a8c9d8');
  const m = new THREE.Mesh(new THREE.PlaneGeometry(wM, hM), texturedMat(tex, 0.9));
  return m;
}

// ── Instanced-part builder ──────────────────────────────────────────────────

export class Parts {
  private byKind = new Map<string, { geo: THREE.BufferGeometry; mat: THREE.Material; mats4: THREE.Matrix4[] }>();
  add(kind: string, geo: () => THREE.BufferGeometry, mat: THREE.Material, m4: THREE.Matrix4): void {
    let b = this.byKind.get(kind);
    if (!b) { b = { geo: geo(), mat, mats4: [] }; this.byKind.set(kind, b); }
    b.mats4.push(m4.clone());
  }
  flush(group: THREE.Group): void {
    for (const b of this.byKind.values()) {
      pinStaticMotionVectors(b.mat); // static furniture: zero object velocity (three's frozen previous instance matrices)
      const im = new THREE.InstancedMesh(b.geo, b.mat, b.mats4.length);
      for (let i = 0; i < b.mats4.length; i++) im.setMatrixAt(i, b.mats4[i]);
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = false;
      im.receiveShadow = true;
      group.add(im);
    }
  }
}

const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

export function mat4(x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number): THREE.Matrix4 {
  _q.setFromAxisAngle(UP, yaw);
  return _m4.compose(_p.set(x, y, z), _q, _s.set(sx, sy, sz));
}

// ── Result records ──────────────────────────────────────────────────────────

export interface DressingCollider { hx: number; hy: number; hz: number; x: number; y: number; z: number; yaw: number }
export interface DressingSolid { x: number; z: number; hx: number; hz: number; yaw: number; base: number; top: number }
export interface DressingSeat { x: number; z: number; r: number }

/**
 * A generated-model stand-in for a procedural dressing part (the hinterland
 * prop wave, 2026-09-07): the builder emits WHERE and HOW BIG, the module
 * instances the cooked `prop:hinter_*` GLB there (seated by its bbox bottom —
 * the PIVOT law — scaled to `targetH` metres tall or `len` metres long).
 * Missing prefab → the builder keeps its procedural geometry (self-heal base).
 */
export interface PrefabPlacement {
  key: string; x: number; y: number; z: number; yaw: number;
  /** Uniform scale from the model's height (metres)… */
  targetH?: number;
  /** …or from its length along local X (metres). */
  len?: number;
  /** Extra vertical factor (crop growth). */
  hMul?: number;
  /** Spun by the module about its own centre (the mill rotor): a plain mesh on a pivot. */
  rotor?: boolean;
  /** Walk collider from the scaled bbox. */
  collide?: boolean;
}

export interface DressingResult {
  group: THREE.Group;
  colliders: DressingCollider[];
  solids: DressingSolid[];
  seats: DressingSeat[];
  rotors: THREE.Object3D[];
  prefabs: PrefabPlacement[];
}

export interface BuildingAnchor {
  wx: number; wz: number; yaw: number;
  w: number; h: number; d: number;
  fn?: string;
  kind: 'building' | 'landmark' | 'prop';
}

export interface DressingInput {
  plan: CityPlan;
  site: SettlementSite;
  seed: string;
  anchors: BuildingAnchor[];
  lampAnchors: LampAnchor[];
  stamps: Array<{ x: number; z: number; hw: number; hd: number; yaw: number; h: number }>;
  /** `prop:hinter_*` keys the catalogue carries — mapped parts become PrefabPlacements instead of geometry. */
  prefabs?: ReadonlySet<string>;
}

// ── Helper geometry probes over the plan ────────────────────────────────────

function padSignedDist(plan: CityPlan, lx: number, lz: number): number {
  let best = Infinity;
  for (const pad of plan.buildings) {
    const f = pad.footprint;
    // bbox precheck
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < f.length; i += 2) {
      if (f[i] < x0) x0 = f[i]; if (f[i] > x1) x1 = f[i];
      if (f[i + 1] < z0) z0 = f[i + 1]; if (f[i + 1] > z1) z1 = f[i + 1];
    }
    if (lx < x0 - 8 || lx > x1 + 8 || lz < z0 - 8 || lz > z1 + 8) continue;
    let inside = false;
    let d = Infinity;
    const n = f.length / 2;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = f[i * 2], zi = f[i * 2 + 1];
      const xj = f[j * 2], zj = f[j * 2 + 1];
      const dx = xj - xi, dz = zj - zi;
      const L2 = dx * dx + dz * dz;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((lx - xi) * dx + (lz - zi) * dz) / L2)) : 0;
      d = Math.min(d, Math.hypot(lx - (xi + dx * t), lz - (zi + dz * t)));
      if ((zi > lz) !== (zj > lz) && lx < ((xj - xi) * (lz - zi)) / (zj - zi + 1e-12) + xi) inside = !inside;
    }
    best = Math.min(best, inside ? -d : d);
    if (best < -2) return best;
  }
  return best;
}

function roadDist(plan: CityPlan, lx: number, lz: number): number {
  let best = Infinity;
  for (const e of plan.roads.edges) {
    const p = e.polyline;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      if (Math.min(ax, bx) - 12 > lx || Math.max(ax, bx) + 12 < lx) continue;
      if (Math.min(az, bz) - 12 > lz || Math.max(az, bz) + 12 < lz) continue;
      const dx = bx - ax, dz = bz - az;
      const L2 = dx * dx + dz * dz;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((lx - ax) * dx + (lz - az) * dz) / L2)) : 0;
      best = Math.min(best, Math.hypot(lx - (ax + dx * t), lz - (az + dz * t)) - e.halfWidth);
    }
  }
  return best;
}

// ── The builder ─────────────────────────────────────────────────────────────

export function buildSettlementDressing(input: DressingInput): DressingResult {
  const { plan, site, seed, anchors, lampAnchors, stamps } = input;
  // Capital wave (2026-09-15): a capital's own works (megawall, gates, plaza)
  // land in phase 3 — its plan is the road skeleton and carries no dressing.
  // A DISTRICT / the campus dress like a city / a town MINUS the edge works
  // (phase 2): no wall, no fingerpost — a district name plank at every exit.
  if (site.tier === 'capital') {
    // Phase 3: the capital's WORKS — the megawall with its gate towers and
    // stairs, and the civic plaza. No town section applies (the plan has no pads).
    const M0 = dressingMats();
    const civ0 = civilizationOf(site.siteSeed);
    const group0 = new THREE.Group();
    group0.name = `dressing:${site.id}`;
    const parts0 = new Parts();
    const colliders0: DressingCollider[] = [], solids0: DressingSolid[] = [], seats0: DressingSeat[] = [];
    buildCapitalWorks({ site, seed, civ: civ0, M: M0, group: group0, parts: parts0, mat4, plank, gy: (x, z) => meshHeight(seed, x, z), colliders: colliders0, solids: solids0, seats: seats0 });
    parts0.flush(group0);
    return { group: group0, colliders: colliders0, solids: solids0, seats: seats0, rotors: [], prefabs: [] };
  }
  const M = dressingMats();
  const civ = civilizationOf(site.siteSeed);
  // The dressing GRAIN of a capital member: a district is city-grained, the campus town-grained.
  const kind: SettlementSite['tier'] = site.tier === 'district' ? 'city' : site.tier === 'circuit' ? 'town' : site.tier;
  const wallMat = civ === 'a' ? M.wallA : M.wallB;
  const clothMat = civ === 'a' ? M.clothA : M.clothB;
  const s = site.siteSeed;
  const group = new THREE.Group();
  group.name = `dressing:${site.id}`;
  const parts = new Parts();
  const colliders: DressingCollider[] = [];
  const solids: DressingSolid[] = [];
  const seats: DressingSeat[] = [];
  const rotors: THREE.Object3D[] = [];
  const prefabs: PrefabPlacement[] = [];
  const want = (k: string): boolean => !!input.prefabs?.has(k);
  const gy = (wx: number, wz: number): number => meshHeight(seed, wx, wz);
  const slots = ((): TownSlot[] => { try { return townSlots(seed, site); } catch { return []; } })();
  const arr = arrivalPoint(seed, site);
  const ports = [spaceportSpot(seed, site), shuttleSpot(seed, site), terminalSpot(seed, site)]
    .filter((p): p is NonNullable<typeof p> => !!p)
    .map((p) => ({ x: site.centerX + p.x, z: site.centerZ + p.z }));
  const PORT_CLEAR = 24;
  const clearOfPorts = (wx: number, wz: number, extra = 0): boolean =>
    Math.hypot(wx - arr.x, wz - arr.z) > 20 + extra
    && ports.every((p) => Math.hypot(wx - p.x, wz - p.z) > PORT_CLEAR + extra);
  // Venue stages stay clear (the civic staging law).
  const clearOfVenues = (wx: number, wz: number, extra = 0): boolean =>
    slots.every((sl) => Math.hypot(wx - sl.x, wz - sl.z) > 6 + extra);

  // ════ 1. EDGE WORKS: walls / palisade / boundary stones + gates ═══════════
  const gates = gatesFor(plan);
  const wallR = plan.config.radius + 8;
  if (site.tier === 'district' || site.tier === 'circuit') {
    // Capital fabric: no town wall, no fingerpost — a name plank at every exit,
    // set beside the connector so the street stays open.
    const names = settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss));
    const name = names.get(site.id) ?? 'District';
    for (const g of gates) {
      const tx = Math.cos(g.ang + Math.PI / 2), tz = Math.sin(g.ang + Math.PI / 2);
      const px = site.centerX + g.x + tx * 6, pz = site.centerZ + g.z + tz * 6;
      const py = gy(px, pz);
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 2.6, 6), M.woodDark);
      post.position.set(px, py + 1.3, pz);
      group.add(post);
      const namePlank = plank(name.toUpperCase(), civ, Math.max(2.2, name.length * 0.3), 0.55);
      namePlank.position.set(px, py + 2.35, pz);
      namePlank.rotation.y = g.ang + Math.PI / 2 + Math.PI; // face the arriving traveller
      group.add(namePlank);
      seats.push({ x: px, z: pz, r: 0.5 });
    }
  } else if (kind !== 'hamlet') {
    const isCity = kind === 'city';
    const wallH = isCity ? 5.6 : 3.6;
    const wallT = isCity ? 1.4 : 0.55;
    const segLen = 11;
    const GATE_HALF_ANG = (isCity ? 9 : 7) / wallR; // opening half-arc
    const stepAng = segLen / wallR;
    const segGeo = (): THREE.BufferGeometry => new THREE.BoxGeometry(1, 1, 1);
    let prev: { x: number; z: number; y: number } | null = null;
    for (let a = 0; a < Math.PI * 2 + 1e-6; a += stepAng) {
      const wx = site.centerX + Math.cos(a) * wallR;
      const wz = site.centerZ + Math.sin(a) * wallR;
      // Openings at gates; gaps at ports/arrival and over water/steep ground.
      const inGate = gates.some((g) => {
        let d = Math.abs(a - ((g.ang + Math.PI * 2) % (Math.PI * 2))) % (Math.PI * 2);
        if (d > Math.PI) d = Math.PI * 2 - d;
        return d < GATE_HALF_ANG;
      });
      const y = gy(wx, wz);
      if (inGate || !clearOfPorts(wx, wz, 6)) { prev = null; continue; }
      if (prev) {
        const midX = (prev.x + wx) / 2, midZ = (prev.z + wz) / 2;
        const L = Math.hypot(wx - prev.x, wz - prev.z);
        const yaw = Math.atan2(wx - prev.x, wz - prev.z) + Math.PI / 2;
        const midY = (prev.y + y) / 2;
        if (Math.abs(prev.y - y) < 4.5) { // a cliff break = a natural wall gap
          parts.add('wall', segGeo, wallMat, mat4(midX, midY + wallH / 2 - 0.3, midZ, yaw, L + 0.4, wallH, wallT));
          // Cap course reads as crenellation-ish trim on the city wall.
          if (isCity) parts.add('wallcap', segGeo, M.stoneDark, mat4(midX, midY + wallH + 0.05, midZ, yaw, L + 0.4, 0.5, wallT + 0.5));
          colliders.push({ hx: L / 2 + 0.2, hy: wallH / 2 + 0.3, hz: wallT / 2, x: midX, y: midY + wallH / 2 - 0.3, z: midZ, yaw });
          solids.push({ x: midX, z: midZ, hx: L / 2 + 0.2, hz: wallT / 2, yaw, base: midY - 1, top: midY + wallH });
        }
      }
      // Posts at joints (towns: palisade posts; cities: pilaster).
      parts.add('wallpost', segGeo, isCity ? M.stoneDark : M.woodDark, mat4(wx, y + wallH / 2, wz, a, wallT + 0.3, wallH + (isCity ? 1.2 : 0.7), wallT + 0.3));
      prev = { x: wx, z: wz, y };
    }
    // Gates: paired towers + lintel + hanging civ banner + night torches.
    for (const g of gates) {
      const gwx = site.centerX + g.x, gwz = site.centerZ + g.z;
      const y = gy(gwx, gwz);
      const tx = Math.cos(g.ang + Math.PI / 2), tz = Math.sin(g.ang + Math.PI / 2);
      const openHalf = isCity ? 7.2 : 5.6;
      const towerH = (isCity ? 9 : 6.2) * (g.main ? 1.2 : 1);
      const towerW = isCity ? 2.6 : 1.7;
      for (const sgn of [-1, 1]) {
        const px = gwx + tx * openHalf * sgn, pz = gwz + tz * openHalf * sgn;
        const py = gy(px, pz);
        parts.add('gatetower', () => new THREE.BoxGeometry(1, 1, 1), wallMat, mat4(px, py + towerH / 2, pz, g.ang, towerW, towerH, towerW));
        parts.add('gatecap', () => new THREE.BoxGeometry(1, 1, 1), M.stoneDark, mat4(px, py + towerH + 0.25, pz, g.ang, towerW + 0.7, 0.5, towerW + 0.7));
        colliders.push({ hx: towerW / 2, hy: towerH / 2, hz: towerW / 2, x: px, y: py + towerH / 2, z: pz, yaw: g.ang });
        solids.push({ x: px, z: pz, hx: towerW / 2, hz: towerW / 2, yaw: g.ang, base: py, top: py + towerH });
        // Torch glow (night) on each tower's inner face.
        const glow = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.2), M.lantern);
        glow.position.set(px - tx * (towerW / 2 + 0.1) * sgn, py + towerH * 0.55, pz - tz * (towerW / 2 + 0.1) * sgn);
        glow.rotation.y = g.ang + (sgn > 0 ? Math.PI : 0);
        group.add(glow);
      }
      // Lintel + banner.
      const lintelY = y + towerH - 0.6;
      parts.add('gatelintel', () => new THREE.BoxGeometry(1, 1, 1), M.woodDark, mat4(gwx, lintelY, gwz, g.ang + Math.PI / 2, openHalf * 2 + towerW, 0.7, 1));
      const banner = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.6), clothMat);
      banner.position.set(gwx, lintelY - 1.6, gwz);
      banner.rotation.y = g.ang;
      group.add(banner);
      // The town sign, just outside the gate, facing the arriving traveler.
      const names = settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss));
      const name = names.get(site.id) ?? 'Settlement';
      const sx = gwx + Math.cos(g.ang) * 9, sz = gwz + Math.sin(g.ang) * 9;
      const sy = gy(sx, sz);
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 2.6, 6), M.woodDark);
      post.position.set(sx, sy + 1.3, sz);
      group.add(post);
      const namePlank = plank(name.toUpperCase(), civ, Math.max(2.2, name.length * 0.34), 0.62);
      namePlank.position.set(sx, sy + 2.35, sz);
      namePlank.rotation.y = g.ang + Math.PI / 2 + Math.PI; // face outward
      group.add(namePlank);
      seats.push({ x: sx, z: sz, r: 0.5 });
      // The MAIN gate carries the fingerpost to the neighbors.
      if (g.main) {
        const sites = settlementSites(seed);
        const neighbors = sites
          .filter((o) => o.id !== site.id)
          .map((o) => ({ o, d: Math.hypot(o.centerX - site.centerX, o.centerZ - site.centerZ) }))
          .sort((x2, y2) => x2.d - y2.d)
          .slice(0, 3);
        const fx = gwx + Math.cos(g.ang) * 12 + tx * 4;
        const fz = gwz + Math.sin(g.ang) * 12 + tz * 4;
        const fy = gy(fx, fz);
        const fpost = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 3.4, 6), M.woodDark);
        fpost.position.set(fx, fy + 1.7, fz);
        group.add(fpost);
        neighbors.forEach(({ o, d }, i) => {
          const nm = names.get(o.id) ?? '?';
          const bearing = Math.atan2(o.centerZ - site.centerZ, o.centerX - site.centerX);
          const fp = plank(`${nm} · ${(d / 1000).toFixed(0)} km`, civ, Math.max(1.7, (nm.length + 6) * 0.19), 0.34);
          fp.position.set(
            fx + Math.cos(bearing) * 0.75,
            fy + 3.1 - i * 0.48,
            fz + Math.sin(bearing) * 0.75,
          );
          fp.rotation.y = -bearing + Math.PI / 2; // plank runs along the bearing
          group.add(fp);
        });
        seats.push({ x: fx, z: fz, r: 0.5 });
      }
    }
  } else {
    // Hamlets: paired boundary stones where the lanes leave, a name stone on
    // the main one — the threshold at village scale.
    const names = settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss));
    for (const g of gates) {
      const gwx = site.centerX + g.x, gwz = site.centerZ + g.z;
      if (!clearOfPorts(gwx, gwz, 4)) continue;
      const tx = Math.cos(g.ang + Math.PI / 2), tz = Math.sin(g.ang + Math.PI / 2);
      for (const sgn of [-1, 1]) {
        const px = gwx + tx * 4.4 * sgn, pz = gwz + tz * 4.4 * sgn;
        const py = gy(px, pz);
        parts.add('boundstone', () => new THREE.DodecahedronGeometry(0.8, 0), M.stone, mat4(px, py + 0.5, pz, h01(s, 40 + Math.round(g.ang * 10)) * Math.PI, 1, 1.25, 1));
        seats.push({ x: px, z: pz, r: 1.1 });
      }
      if (g.main) {
        const nm = names.get(site.id) ?? 'Hamlet';
        const pl = plank(nm.toUpperCase(), civ, Math.max(1.8, nm.length * 0.3), 0.5);
        const py = gy(gwx, gwz);
        pl.position.set(gwx + tx * 4.4, py + 1.6, gwz + tz * 4.4);
        pl.rotation.y = g.ang + Math.PI / 2 + Math.PI;
        group.add(pl);
      }
    }
  }

  // ════ 2. WELLS + TROUGH: the well venue finally rendered ══════════════════
  const wellSlot = slots.find((sl) => sl.key === 'well');
  if (wellSlot) {
    // Clamp the well out of any pad (venue slots can land inside fabric).
    let wwx = wellSlot.x, wwz = wellSlot.z;
    for (let pass = 0; pass < 6; pass++) {
      const sd = padSignedDist(plan, wwx - site.centerX, wwz - site.centerZ);
      if (sd > 1.6) break;
      const e = 0.5;
      const gx = padSignedDist(plan, wwx - site.centerX + e, wwz - site.centerZ) - padSignedDist(plan, wwx - site.centerX - e, wwz - site.centerZ);
      const gz2 = padSignedDist(plan, wwx - site.centerX, wwz - site.centerZ + e) - padSignedDist(plan, wwx - site.centerX, wwz - site.centerZ - e);
      const gl = Math.hypot(gx, gz2) || 1;
      wwx += (gx / gl) * (1.6 - sd);
      wwz += (gz2 / gl) * (1.6 - sd);
    }
    const wy = gy(wwx, wwz);
    const wyaw = h01(s, 1201) * Math.PI * 2;
    if (want('prop:hinter_well')) {
      prefabs.push({ key: 'prop:hinter_well', x: wwx, y: wy, z: wwz, yaw: wyaw, targetH: 3.0 });
    } else {
    // Stone ring + posts + little roof + winch bar + dark water disc.
    parts.add('wellring', () => new THREE.CylinderGeometry(1.05, 1.15, 0.85, 10, 1, true), M.stone, mat4(wwx, wy + 0.42, wwz, 0, 1, 1, 1));
    const waterDisc = new THREE.Mesh(new THREE.CircleGeometry(0.95, 12), M.water);
    waterDisc.rotation.x = -Math.PI / 2;
    waterDisc.position.set(wwx, wy + 0.55, wwz);
    group.add(waterDisc);
    for (const sgn of [-1, 1]) {
      parts.add('wellpost', () => new THREE.BoxGeometry(0.14, 2.1, 0.14), M.woodDark,
        mat4(wwx + Math.cos(wyaw) * 1.05 * sgn, wy + 1.05, wwz + Math.sin(wyaw) * 1.05 * sgn, wyaw, 1, 1, 1));
    }
    parts.add('wellbar', () => new THREE.CylinderGeometry(0.06, 0.06, 2.3, 6), M.wood, ((): THREE.Matrix4 => {
      _q.setFromAxisAngle(new THREE.Vector3(Math.sin(wyaw + Math.PI / 2), 0, Math.cos(wyaw + Math.PI / 2)), Math.PI / 2);
      return _m4.compose(_p.set(wwx, wy + 1.95, wwz), _q, _s.set(1, 1, 1));
    })());
    parts.add('wellroof', () => new THREE.ConeGeometry(1.7, 0.8, 4), M.woodDark, mat4(wwx, wy + 2.55, wwz, wyaw + Math.PI / 4, 1, 1, 1));
    }
    colliders.push({ hx: 1.15, hy: 0.6, hz: 1.15, x: wwx, y: wy + 0.45, z: wwz, yaw: 0 });
    seats.push({ x: wwx, z: wwz, r: 1.9 });
  }
  // Market trough (pack animals & hovercraft alike drink somewhere).
  const marketSlot = slots.find((sl) => sl.key === 'market');
  if (marketSlot && kind !== 'hamlet') {
    const ta = h01(s, 1301) * Math.PI * 2;
    const twx = marketSlot.x + Math.cos(ta) * 9.5, twz = marketSlot.z + Math.sin(ta) * 9.5;
    if (padSignedDist(plan, twx - site.centerX, twz - site.centerZ) > 1.2 && clearOfVenues(twx, twz)) {
      const ty = gy(twx, twz);
      if (want('prop:hinter_trough')) prefabs.push({ key: 'prop:hinter_trough', x: twx, y: ty, z: twz, yaw: ta, len: 2.2, collide: true });
      else parts.add('trough', () => new THREE.BoxGeometry(2.2, 0.55, 0.8), M.woodDark, mat4(twx, ty + 0.28, twz, ta, 1, 1, 1));
      const tw = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 0.62), M.water);
      tw.rotation.x = -Math.PI / 2;
      tw.rotation.z = -ta;
      tw.position.set(twx, ty + 0.5, twz);
      group.add(tw);
      colliders.push({ hx: 1.1, hy: 0.3, hz: 0.4, x: twx, y: ty + 0.28, z: twz, yaw: ta });
      seats.push({ x: twx, z: twz, r: 1.4 });
    }
  }

  // ════ 3. PLAZA CHARACTER + map board + lantern strings ════════════════════
  const plazaSlot = slots.find((sl) => sl.key === 'plaza');
  if (plazaSlot) {
    const px = plazaSlot.x, pz = plazaSlot.z;
    const py = gy(px, pz);
    const plazaType = (['market', 'civic', 'faith'] as const)[Math.floor(h01(s, 1401) * 3) % 3];
    if (plazaType === 'civic') {
      // Twin civ flag poles + the speaker's plinth at the heart's edge.
      for (const sgn of [-1, 1]) {
        const fx = px + 15.5 * sgn, fz = pz + 3.5;
        const fy = gy(fx, fz);
        parts.add('flagpole', () => new THREE.CylinderGeometry(0.07, 0.1, 7.5, 6), M.stoneDark, mat4(fx, fy + 3.75, fz, 0, 1, 1, 1));
        const flag = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 1.1), clothMat);
        flag.position.set(fx + 0.95, fy + 6.7, fz);
        group.add(flag);
        seats.push({ x: fx, z: fz, r: 0.5 });
      }
      const plinthX = px, plinthZ = pz - 15.5;
      parts.add('plinth', () => new THREE.BoxGeometry(1.7, 0.55, 1.7), M.stone, mat4(plinthX, gy(plinthX, plinthZ) + 0.28, plinthZ, h01(s, 1402), 1, 1, 1));
      seats.push({ x: plinthX, z: plinthZ, r: 1.3 });
    } else if (plazaType === 'faith') {
      // A ring of standing stones + the night brazier — the quiet square.
      const n = 5;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + h01(s, 1403);
        const sx2 = px + Math.cos(a) * 16.5, sz2 = pz + Math.sin(a) * 16.5;
        const sy2 = gy(sx2, sz2);
        parts.add('standstone', () => new THREE.BoxGeometry(0.8, 2.5, 0.5), M.stoneDark, mat4(sx2, sy2 + 1.2, sz2, a + Math.PI / 2, 1, 0.85 + h01(s, 1404 + i) * 0.4, 1));
        seats.push({ x: sx2, z: sz2, r: 0.9 });
      }
      const bx = px + 15.5, bz = pz - 2;
      const by = gy(bx, bz);
      if (want('prop:hinter_brazier')) prefabs.push({ key: 'prop:hinter_brazier', x: bx, y: by, z: bz, yaw: h01(s, 1281) * 6, targetH: 1.25, collide: true });
      else parts.add('brazier', () => new THREE.CylinderGeometry(0.55, 0.4, 1.0, 8), M.stoneDark, mat4(bx, by + 0.5, bz, 0, 1, 1, 1));
      const fireGlow = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 1.5), M.lantern);
      fireGlow.position.set(bx, by + 1.6, bz);
      group.add(fireGlow);
      seats.push({ x: bx, z: bz, r: 1.0 });
    }
    // (market plazas keep their stall-heavy furniture character.)
    // The MAP BOARD, mirroring the notice board across the stage (board at
    // +6,+4 — the map at −6,+4 keeps the heart's rim symmetric).
    const mbx = px - 6, mbz = pz + 4;
    const mby = gy(mbx, mbz);
    for (const sgn of [-1, 1]) {
      parts.add('boardpost', () => new THREE.BoxGeometry(0.14, 2.3, 0.14), M.woodDark, mat4(mbx + 0.85 * sgn, mby + 1.15, mbz, 0, 1, 1, 1));
    }
    const mapMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 1.35), texturedMat(planMapTexture(plan, site, seed), 0.85));
    mapMesh.position.set(mbx, mby + 1.65, mbz);
    mapMesh.rotation.y = Math.atan2(px - mbx, pz - mbz);
    group.add(mapMesh);
    seats.push({ x: mbx, z: mbz, r: 1.0 });
    // Lantern strings between the plaza's own lamps (festival warmth after
    // dark — the string sags between lamp heads, lanterns glow at night).
    const plazaLamps = lampAnchors.filter((l) => Math.hypot(l.x - px, l.z - pz) < 30).slice(0, 7);
    for (let i = 0; i + 1 < plazaLamps.length && i < 5; i++) {
      const a = plazaLamps[i], b = plazaLamps[i + 1];
      const span = Math.hypot(b.x - a.x, b.z - a.z);
      if (span < 6 || span > 34) continue;
      const segsN = 6;
      for (let k = 0; k < segsN; k++) {
        const t0 = k / segsN, t1 = (k + 1) / segsN;
        const sag = (t: number): number => -Math.sin(t * Math.PI) * span * 0.06;
        const x0 = a.x + (b.x - a.x) * t0, z0 = a.z + (b.z - a.z) * t0, y0 = a.y + (b.y - a.y) * t0 + sag(t0);
        const x1 = a.x + (b.x - a.x) * t1, z1 = a.z + (b.z - a.z) * t1, y1 = a.y + (b.y - a.y) * t1 + sag(t1);
        const L = Math.hypot(x1 - x0, y1 - y0, z1 - z0);
        const wire = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, L, 3), M.woodDark);
        wire.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
        wire.lookAt(x1, y1, z1);
        wire.rotateX(Math.PI / 2);
        group.add(wire);
        // A lantern at each interior joint.
        if (k > 0) {
          const lm = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.44), M.lantern);
          lm.position.set(x0, y0 - 0.28, z0);
          group.add(lm);
        }
      }
    }
  }

  // ════ 4. ACTIVE FRONTAGES: awnings, trade signs, window glow ══════════════
  const TRADE_LABEL: Record<string, string> = {
    market: '◆ MARKET', commerce: '◆ TRADER', entertainment: '◆ TAVERN',
    manufacturing: '◆ SMITHY', industry: '◆ WORKS', medical: '◆ CLINIC',
    science: '◆ ARCHIVE', agriculture: '◆ GRANGE',
  };
  let frontN = 0;
  const FRONT_CAP = kind === 'city' ? 34 : kind === 'town' ? 18 : 8;
  for (const a of anchors) {
    if (frontN >= FRONT_CAP) break;
    if (a.kind !== 'building' || !a.fn) continue;
    const label = TRADE_LABEL[a.fn];
    if (!label) continue;
    if (a.h > 90) continue; // towers meet the street through their podium — skip supertalls
    const fx = Math.sin(a.yaw), fz = Math.cos(a.yaw);
    const doorX = a.wx + fx * (a.d / 2 + 0.25);
    const doorZ = a.wz + fz * (a.d / 2 + 0.25);
    const doorY = gy(doorX, doorZ);
    if (!clearOfVenues(doorX, doorZ) || !clearOfPorts(doorX, doorZ)) continue;
    // Awning: a canted canvas over the door.
    const awn = new THREE.Mesh(new THREE.PlaneGeometry(3.0, 1.5), h01(s, 1500 + frontN) < 0.5 ? clothMat : M.clothWhite);
    awn.position.set(doorX + fx * 0.7, doorY + 2.75, doorZ + fz * 0.7);
    awn.rotation.y = a.yaw;
    awn.rotateX(-0.5);
    group.add(awn);
    // The hanging trade sign, perpendicular to the facade.
    const signPl = plank(label, civ, 1.5, 0.4);
    signPl.position.set(doorX + fx * 0.5, doorY + 3.6, doorZ + fz * 0.5);
    signPl.rotation.y = a.yaw + Math.PI / 2;
    group.add(signPl);
    // Window glow: two street-level quads flanking the door (night only).
    for (const sgn of [-1, 1]) {
      const wgx = a.wx + fx * (a.d / 2 + 0.12) + Math.cos(a.yaw) * 2.1 * sgn;
      const wgz = a.wz + fz * (a.d / 2 + 0.12) - Math.sin(a.yaw) * 2.1 * sgn;
      const wg = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 1.4), M.glow);
      wg.position.set(wgx, doorY + 1.9, wgz);
      wg.rotation.y = a.yaw;
      group.add(wg);
    }
    // Goods out front: a barrel pair or a goods table at working trades.
    if ((a.fn === 'commerce' || a.fn === 'market') && h01(s, 1550 + frontN) < 0.7) {
      const gxx = doorX + Math.cos(a.yaw) * 2.6, gzz = doorZ - Math.sin(a.yaw) * 2.6;
      const gyy = gy(gxx, gzz);
      if (want('prop:hinter_barrel')) {
        // the generated drum lies on its side on feet (1.9 × 1.2 × 1.2 raw): scale by LENGTH, not height
        prefabs.push({ key: 'prop:hinter_barrel', x: gxx, y: gyy, z: gzz, yaw: 0, len: 1.2 });
        prefabs.push({ key: 'prop:hinter_barrel', x: gxx + 0.9, y: gyy, z: gzz + 0.35, yaw: 1.1, len: 1.0 });
      } else {
      parts.add('barrel', () => new THREE.CylinderGeometry(0.42, 0.46, 0.95, 8), M.wood, mat4(gxx, gyy + 0.47, gzz, 0, 1, 1, 1));
      parts.add('barrel', () => new THREE.CylinderGeometry(0.42, 0.46, 0.95, 8), M.wood, mat4(gxx + 0.8, gyy + 0.47, gzz + 0.3, 0, 0.9, 0.85, 0.9));
      }
      seats.push({ x: gxx, z: gzz, r: 1.1 });
    }
    frontN++;
  }

  // ════ 5. GARDEN-EDGE YARDS (the transect's outer band, lived-in) ══════════
  let yardN = 0;
  const YARD_CAP = kind === 'city' ? 20 : kind === 'town' ? 14 : 8;
  const R = Math.max(1, plan.config.radius);
  for (const a of anchors) {
    if (yardN >= YARD_CAP) break;
    if (a.kind !== 'building') continue;
    if (a.fn !== 'habitation' && a.fn !== 'habitation_luxury') continue;
    const distFrac = Math.hypot(a.wx - site.centerX, a.wz - site.centerZ) / R;
    if (transectBand(distFrac) !== 'edge') continue;
    if (a.h > 40) continue;
    const roll = h01(s, 1600 + yardN * 7);
    const fx = Math.sin(a.yaw), fz = Math.cos(a.yaw);
    // Yard fence: posts + rails around the building's rear half, clipped
    // against roads/pads by sampling each post.
    const hw = a.w / 2 + 2.2, hd = a.d / 2 + 2.6;
    const postGeo = (): THREE.BufferGeometry => new THREE.BoxGeometry(0.12, 1.0, 0.12);
    const railGeo = (): THREE.BufferGeometry => new THREE.BoxGeometry(1, 0.08, 0.06);
    let prevPost: { x: number; z: number; y: number } | null = null;
    const postStep = 2.6;
    const perim: Array<[number, number]> = [];
    for (let t = -hw; t <= hw; t += postStep) perim.push([t, -hd]);
    for (let t = -hd; t <= hd; t += postStep) perim.push([hw, t]);
    for (let t = hw; t >= -hw; t -= postStep) perim.push([t, hd]);
    for (const [lx, lz] of perim) {
      const cos = Math.cos(a.yaw), sin = Math.sin(a.yaw);
      const wx2 = a.wx + lx * cos - lz * sin;
      const wz2 = a.wz + lx * sin + lz * cos;
      const plx = wx2 - site.centerX, plz = wz2 - site.centerZ;
      if (roadDist(plan, plx, plz) < 1.4 || padSignedDist(plan, plx, plz) < -0.4) { prevPost = null; continue; }
      const y2 = gy(wx2, wz2);
      parts.add('fencepost', postGeo, M.woodDark, mat4(wx2, y2 + 0.5, wz2, a.yaw, 1, 1, 1));
      if (prevPost && Math.hypot(wx2 - prevPost.x, wz2 - prevPost.z) < postStep * 1.5) {
        const midX = (wx2 + prevPost.x) / 2, midZ = (wz2 + prevPost.z) / 2;
        const L = Math.hypot(wx2 - prevPost.x, wz2 - prevPost.z);
        const ryaw = Math.atan2(wx2 - prevPost.x, wz2 - prevPost.z) + Math.PI / 2;
        parts.add('fencerail', railGeo, M.wood, mat4(midX, (y2 + prevPost.y) / 2 + 0.72, midZ, ryaw, L, 1, 1));
        parts.add('fencerail', railGeo, M.wood, mat4(midX, (y2 + prevPost.y) / 2 + 0.38, midZ, ryaw, L, 1, 1));
      }
      prevPost = { x: wx2, z: wz2, y: y2 };
    }
    // Yard clutter: the hash picks the household's story — a laundry line, a
    // wood pile, or a hand cart.
    const bx = a.wx - fx * (a.d / 2 + 1.8);
    const bz = a.wz - fz * (a.d / 2 + 1.8);
    const by = gy(bx, bz);
    if (roll < 0.4) {
      // Laundry line: two posts, a wire, three cloth squares.
      const lyaw = a.yaw + Math.PI / 2;
      const lx2 = Math.sin(lyaw), lz2 = Math.cos(lyaw);
      for (const sgn of [-1, 1]) {
        parts.add('linepost', postGeo, M.woodDark, mat4(bx + lx2 * 2.2 * sgn, by + 0.9, bz + lz2 * 2.2 * sgn, lyaw, 1, 1.8, 1));
      }
      for (let ci = 0; ci < 3; ci++) {
        const t = -0.55 + ci * 0.55;
        const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.65), ci === 1 ? clothMat : M.clothWhite);
        cloth.position.set(bx + lx2 * 2.2 * t, by + 1.62, bz + lz2 * 2.2 * t);
        cloth.rotation.y = lyaw;
        group.add(cloth);
      }
    } else if (roll < 0.7) {
      // Wood pile.
      for (let wi = 0; wi < 6; wi++) {
        const ox = (h01(s, 1700 + yardN * 11 + wi) - 0.5) * 1.1;
        const oz = (h01(s, 1750 + yardN * 11 + wi) - 0.5) * 0.6;
        parts.add('log', () => new THREE.CylinderGeometry(0.14, 0.14, 1.2, 5), M.wood, ((): THREE.Matrix4 => {
          _q.setFromAxisAngle(new THREE.Vector3(Math.sin(a.yaw + Math.PI / 2), 0, Math.cos(a.yaw + Math.PI / 2)), Math.PI / 2);
          return _m4.compose(_p.set(bx + ox, by + 0.15 + (wi >= 3 ? 0.27 : 0), bz + oz), _q, _s.set(1, 1, 1));
        })());
      }
      seats.push({ x: bx, z: bz, r: 1.0 });
    } else {
      // Hand cart: a box bed on two cylinder wheels, handles down.
      if (want('prop:hinter_cart')) prefabs.push({ key: 'prop:hinter_cart', x: bx, y: by, z: bz, yaw: a.yaw + 0.35, len: 2.0, collide: true });
      else {
      parts.add('cartbed', () => new THREE.BoxGeometry(1.5, 0.35, 0.9), M.wood, mat4(bx, by + 0.62, bz, a.yaw + 0.35, 1, 1, 1));
      for (const sgn of [-1, 1]) {
        parts.add('cartwheel', () => new THREE.CylinderGeometry(0.4, 0.4, 0.1, 8), M.woodDark, ((): THREE.Matrix4 => {
          _q.setFromAxisAngle(new THREE.Vector3(Math.sin(a.yaw + 0.35), 0, Math.cos(a.yaw + 0.35)), Math.PI / 2);
          return _m4.compose(_p.set(bx + Math.cos(a.yaw + 0.35) * 0.5 * sgn, by + 0.4, bz - Math.sin(a.yaw + 0.35) * 0.5 * sgn), _q, _s.set(1, 1, 1));
        })());
      }
      }
      seats.push({ x: bx, z: bz, r: 1.1 });
    }
    yardN++;
  }

  // ════ 6. TERRACES: retaining walls + stair blocks at stamp edges ══════════
  let terrN = 0;
  for (const st of stamps) {
    if (terrN >= 20) break;
    const cos = Math.cos(st.yaw), sin = Math.sin(st.yaw);
    // Probe the midpoint of each rect edge, just outside.
    const edges: Array<[number, number, number]> = [
      [0, st.hd + 1.2, 0], [0, -st.hd - 1.2, Math.PI],
      [st.hw + 1.2, 0, Math.PI / 2], [-st.hw - 1.2, 0, -Math.PI / 2],
    ];
    for (const [lx, lz] of edges) {
      const wx2 = st.x + lx * cos - lz * sin;
      const wz2 = st.z + lx * sin + lz * cos;
      const outerY = gy(wx2, wz2);
      const drop = st.h - outerY;
      if (drop < 1.3 || drop > 14) continue;
      const edgeLen = Math.abs(lz) > 0.1 ? st.hw * 2 : st.hd * 2;
      const eyaw = st.yaw + (Math.abs(lz) > 0.1 ? 0 : Math.PI / 2);
      parts.add('retain', () => new THREE.BoxGeometry(1, 1, 1), M.stoneDark,
        mat4((st.x + wx2) / 2, outerY + drop / 2 - 0.1, (st.z + wz2) / 2, eyaw, edgeLen * 0.92, drop + 0.4, 0.6));
      // A stair block down the middle when the drop is walkable.
      if (drop < 3.4) {
        const stepsN = Math.max(2, Math.ceil(drop / 0.28));
        for (let si = 0; si < stepsN; si++) {
          const t = si / stepsN;
          parts.add('stair', () => new THREE.BoxGeometry(1.6, 0.26, 0.34), M.stone,
            mat4(wx2 + (wx2 - st.x) * t * 0.35, st.h - drop * t - 0.13, wz2 + (wz2 - st.z) * t * 0.35, eyaw, 1, 1, 1));
        }
      }
      terrN++;
      if (terrN >= 20) break;
    }
  }

  // ════ 7. STREET TREES + POCKET GREENS (the green fabric readmitted) ═══════
  const treeTrunk = (): THREE.BufferGeometry => new THREE.CylinderGeometry(0.14, 0.2, 2.2, 6);
  const treeCanopy = (): THREE.BufferGeometry => new THREE.ConeGeometry(1.5, 3.2, 7);
  let treeN = 0;
  const TREE_CAP = kind === 'city' ? 90 : kind === 'town' ? 44 : 16;
  // Along arterial+collector furnishing zones, offset from the lamp rhythm.
  for (const e of plan.roads.edges) {
    if (treeN >= TREE_CAP) break;
    if (e.roadClass === 'local') continue;
    const p = e.polyline;
    const off = e.halfWidth + Math.max(1.4, plan.config.roads.sidewalkWidth * 0.6);
    for (let i = 0; i + 3 < p.length && treeN < TREE_CAP; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      const L = Math.hypot(bx - ax, bz - az);
      const step = 26;
      for (let d = 13 + (h01(s, 1800 + e.id) * 8); d < L - 6 && treeN < TREE_CAP; d += step) {
        const t = d / L;
        const dx = (bx - ax) / L, dz = (bz - az) / L;
        const side = (Math.floor(d / step) % 2 === 0) ? 1 : -1;
        const lx = ax + (bx - ax) * t + -dz * off * side;
        const lz = az + (bz - az) * t + dx * off * side;
        if (padSignedDist(plan, lx, lz) < 2.2) continue;
        if (roadDist(plan, lx, lz) < 1.0) continue;
        if (!clearOfVenues(site.centerX + lx, site.centerZ + lz, 1)) continue;
        if (!clearOfPorts(site.centerX + lx, site.centerZ + lz)) continue;
        const wx2 = site.centerX + lx, wz2 = site.centerZ + lz;
        const y2 = gy(wx2, wz2);
        const sc = 0.85 + h01(s, 1820 + treeN) * 0.5;
        const dark = h01(s, 1830 + treeN) >= 0.5;
        const treeKey = dark ? 'prop:hinter_shade_tree_b' : 'prop:hinter_shade_tree_a';
        if (want(treeKey)) prefabs.push({ key: treeKey, x: wx2, y: y2, z: wz2, yaw: h01(s, 1840 + treeN) * 6.28, targetH: 5.4 * sc, collide: true });
        else {
        parts.add('treetrunk', treeTrunk, M.woodDark, mat4(wx2, y2 + 1.1 * sc, wz2, 0, sc, sc, sc));
        parts.add('treecanopy', treeCanopy, dark ? M.leafDark : M.leafGreen, mat4(wx2, y2 + (2.2 + 1.6) * sc, wz2, 0, sc, sc, sc));
        }
        seats.push({ x: wx2, z: wz2, r: 0.7 });
        treeN++;
      }
    }
  }
  // Plaza shade ring: trees just outside the seat ring.
  if (plazaSlot) {
    const n = kind === 'city' ? 8 : kind === 'town' ? 6 : 4;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + h01(s, 1901);
      const rr = 24 + h01(s, 1902 + i) * 3;
      const wx2 = plazaSlot.x + Math.cos(a) * rr;
      const wz2 = plazaSlot.z + Math.sin(a) * rr;
      const lx = wx2 - site.centerX, lz = wz2 - site.centerZ;
      if (padSignedDist(plan, lx, lz) < 2.0 || roadDist(plan, lx, lz) < 1.0) continue;
      if (!clearOfVenues(wx2, wz2, 1) || !clearOfPorts(wx2, wz2)) continue;
      const y2 = gy(wx2, wz2);
      const sc = 1.0 + h01(s, 1910 + i) * 0.4;
      if (want('prop:hinter_shade_tree_a')) prefabs.push({ key: 'prop:hinter_shade_tree_a', x: wx2, y: y2, z: wz2, yaw: h01(s, 1920 + i) * 6.28, targetH: 5.6 * sc, collide: true });
      else {
      parts.add('treetrunk', treeTrunk, M.woodDark, mat4(wx2, y2 + 1.1 * sc, wz2, 0, sc, sc, sc));
      parts.add('treecanopy', treeCanopy, M.leafGreen, mat4(wx2, y2 + 3.8 * sc, wz2, 0, sc * 1.15, sc, sc * 1.15));
      }
      seats.push({ x: wx2, z: wz2, r: 0.7 });
    }
  }

  // ════ 8. THE LOOKOUT (high-relief sites get their terrace) ════════════════
  {
    let lo = -Infinity, hi = -Infinity, hiA = 0;
    const base = gy(site.centerX, site.centerZ);
    lo = base;
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * Math.PI * 2;
      const h = gy(site.centerX + Math.cos(a) * R * 0.95, site.centerZ + Math.sin(a) * R * 0.95);
      if (h > hi) { hi = h; hiA = a; }
      if (h < lo) lo = h;
    }
    if (hi - lo > 24 && kind !== 'city') {
      const wx2 = site.centerX + Math.cos(hiA) * R * 0.95;
      const wz2 = site.centerZ + Math.sin(hiA) * R * 0.95;
      if (clearOfPorts(wx2, wz2, 8)) {
        const y2 = gy(wx2, wz2);
        parts.add('lookdeck', () => new THREE.CylinderGeometry(3.4, 3.6, 0.5, 10), M.stone, mat4(wx2, y2 + 0.25, wz2, 0, 1, 1, 1));
        for (let k = 0; k < 10; k++) {
          const a = (k / 10) * Math.PI * 2;
          parts.add('lookrail', () => new THREE.BoxGeometry(0.1, 1.1, 0.1), M.woodDark, mat4(wx2 + Math.cos(a) * 3.3, y2 + 1.0, wz2 + Math.sin(a) * 3.3, a, 1, 1, 1));
        }
        parts.add('lookbench', () => new THREE.BoxGeometry(1.6, 0.45, 0.5), M.wood, mat4(wx2, y2 + 0.72, wz2, hiA + Math.PI, 1, 1, 1));
        colliders.push({ hx: 3.5, hy: 0.3, hz: 3.5, x: wx2, y: y2 + 0.25, z: wz2, yaw: 0 });
        seats.push({ x: wx2, z: wz2, r: 2.4 });
      }
    }
  }

  // ════ 9. THE PROCESSIONAL ROUTE: banner line along the parade spine ═══════
  const route = ceremonialRoute(plan, site.siteSeed);
  if (route && kind !== 'hamlet') {
    let bn = 0;
    for (let i = 0; i + 1 < route.path.length && bn < 10; i++) {
      const a = route.path[i], b = route.path[i + 1];
      const L = Math.hypot(b.x - a.x, b.z - a.z);
      if (L < 20) continue;
      for (let d = 14; d < L - 8 && bn < 10; d += 42) {
        const t = d / L;
        const dx = (b.x - a.x) / L, dz = (b.z - a.z) / L;
        for (const sgn of [-1, 1]) {
          const lx = a.x + (b.x - a.x) * t + -dz * 5.4 * sgn;
          const lz = a.z + (b.z - a.z) * t + dx * 5.4 * sgn;
          if (padSignedDist(plan, lx, lz) < 1.2 || roadDist(plan, lx, lz) < 0.5) continue;
          const wx2 = site.centerX + lx, wz2 = site.centerZ + lz;
          if (!clearOfVenues(wx2, wz2) || !clearOfPorts(wx2, wz2)) continue;
          const y2 = gy(wx2, wz2);
          parts.add('procpole', () => new THREE.CylinderGeometry(0.06, 0.09, 4.6, 6), M.woodDark, mat4(wx2, y2 + 2.3, wz2, 0, 1, 1, 1));
          const bnr = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 1.7), clothMat);
          bnr.position.set(wx2, y2 + 3.6, wz2);
          bnr.rotation.y = Math.atan2(dx, dz);
          group.add(bnr);
          seats.push({ x: wx2, z: wz2, r: 0.4 });
        }
        bn++;
      }
    }
  }

  // The Grand Circuit's campus (phase 5): grandstands, pit lane, garages, tower, gantry — on top of the town-grain dressing.
  if (site.tier === 'circuit') buildCircuitWorks({ site, seed, civ, M, group, parts, mat4, plank, gy, colliders, solids, seats });
  parts.flush(group);
  return { group, colliders, solids, seats, rotors, prefabs };
}

// ── The plaza map board texture (a real map of THIS town) ───────────────────

const mapTexCache = new Map<number, THREE.CanvasTexture>();

function planMapTexture(plan: CityPlan, site: SettlementSite, seed: string): THREE.CanvasTexture {
  const hit = mapTexCache.get(site.siteSeed);
  if (hit) return hit;
  const W = 512, H = 384;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const civ = civilizationOf(site.siteSeed);
  ctx.fillStyle = civ === 'a' ? '#e8dcc0' : '#d5e2e8';
  ctx.fillRect(0, 0, W, H);
  const R = plan.config.radius * 1.15;
  const px = (x: number): number => W / 2 + (x / R) * (W / 2 - 8);
  const pz = (z: number): number => H / 2 + (z / R) * (H / 2 - 8);
  // Pads.
  ctx.fillStyle = civ === 'a' ? '#c9b183' : '#a9bfc9';
  for (const pad of plan.buildings) {
    const f = pad.footprint;
    ctx.beginPath();
    ctx.moveTo(px(f[0]), pz(f[1]));
    for (let i = 2; i < f.length; i += 2) ctx.lineTo(px(f[i]), pz(f[i + 1]));
    ctx.closePath();
    ctx.fill();
  }
  // Roads.
  ctx.strokeStyle = '#6b5b40';
  for (const e of plan.roads.edges) {
    ctx.lineWidth = e.roadClass === 'arterial' ? 4 : e.roadClass === 'collector' ? 2.5 : 1.4;
    const p = e.polyline;
    ctx.beginPath();
    ctx.moveTo(px(p[0]), pz(p[1]));
    for (let i = 2; i < p.length; i += 2) ctx.lineTo(px(p[i]), pz(p[i + 1]));
    ctx.stroke();
  }
  // Venues + you-are-here.
  try {
    ctx.font = 'bold 13px Georgia, serif';
    for (const slot of townSlots(seed, site)) {
      const lx = slot.x - site.centerX, lz = slot.z - site.centerZ;
      ctx.fillStyle = '#8c1f1f';
      ctx.beginPath();
      ctx.arc(px(lx), pz(lz), 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#2c2416';
      ctx.fillText(civ === 'a' ? slot.nameA : slot.nameB, px(lx) + 7, pz(lz) + 4);
    }
  } catch { /* siteless */ }
  // Named streets (the quarter names made legible).
  try {
    ctx.font = 'italic 12px Georgia, serif';
    ctx.fillStyle = '#4a3d28';
    let names = 0;
    for (const [eid, sn] of streetNames(plan, site.siteSeed)) {
      if (names >= 6) break;
      const e = plan.roads.edges.find((x) => x.id === eid);
      if (!e) continue;
      const p = e.polyline;
      const mx = (p[0] + p[p.length - 2]) / 2, mz = (p[1] + p[p.length - 1]) / 2;
      ctx.fillText(sn.name, px(mx) - 20, pz(mz) - 4);
      names++;
    }
  } catch { /* best-effort */ }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  mapTexCache.set(site.siteSeed, tex);
  return tex;
}
